// A step factory that fills every StepSchema field with its default, so a
// template can name just the fields that matter and still be a valid ProcedureDoc.
function step(seq, name, extra = {}) {
    return {
        seq,
        name,
        instruction: '',
        referenceImageKey: null,
        referenceClipKey: null,
        expectedAction: 'other',
        critical: false,
        timeoutS: null,
        verifyBy: 'presence',
        standardTimeS: null,
        acceptableTimeS: null,
        parallelGroup: null,
        components: [],
        rois: [],
        ...extra,
    };
}
function comp(partNumber, description = '', quantity = 1) {
    return { partNumber, description, quantity, binLocation: null, partId: null };
}
// A component-picking step verified by reading the part's label.
function pick(seq, name, part, opts = { std: 8, acc: 15 }) {
    return step(seq, name, {
        expectedAction: 'pick',
        verifyBy: opts.verifyBy ?? 'label',
        components: [part],
        standardTimeS: opts.std,
        acceptableTimeS: opts.acc,
    });
}
export const PROCGUARD_TEMPLATES = [
    {
        key: 'kit-3-part',
        name: '3-part kit assembly',
        description: 'Pick a bolt, a nut and a washer into a kit tray — each verified by its label.',
        category: 'Kitting',
        doc: {
            name: '3-part kit assembly',
            description: 'A worked kitting example: three labelled parts picked into a tray, verified by reading each label. Draw an ROI over the source bin/part for every step on your camera.',
            cycleTimeTargetS: 60,
            steps: [
                pick(1, 'Pick M6×20 bolt', comp('M6-20', 'Hex bolt M6×20', 1), { std: 8, acc: 15 }),
                pick(2, 'Pick M6 nut', comp('NUT-M6', 'Hex nut M6', 1), { std: 8, acc: 15 }),
                pick(3, 'Pick M6 washer', comp('WASHER-6', 'Flat washer M6', 1), { std: 8, acc: 15 }),
                step(4, 'Confirm kit complete in tray', { expectedAction: 'inspect', critical: true, standardTimeS: 5, acceptableTimeS: 10 }),
            ],
        },
    },
    {
        key: 'bolt-torque-4',
        name: 'Bolt torque sequence (4-bolt star)',
        description: 'Fasten four bolts in a star pattern, each an activity + timing check.',
        category: 'Assembly',
        doc: {
            name: 'Bolt torque sequence (4-bolt star)',
            description: 'Tighten four bolts in the correct star order. Verified by activity (a hand at each position) and timing — draw one ROI per bolt position.',
            cycleTimeTargetS: 45,
            steps: [
                step(1, 'Torque bolt 1 (top-left)', { expectedAction: 'fasten', standardTimeS: 6, acceptableTimeS: 12 }),
                step(2, 'Torque bolt 2 (bottom-right)', { expectedAction: 'fasten', standardTimeS: 6, acceptableTimeS: 12 }),
                step(3, 'Torque bolt 3 (top-right)', { expectedAction: 'fasten', standardTimeS: 6, acceptableTimeS: 12 }),
                step(4, 'Torque bolt 4 (bottom-left)', { expectedAction: 'fasten', critical: true, standardTimeS: 6, acceptableTimeS: 12 }),
            ],
        },
    },
    {
        key: 'subassembly-label',
        name: 'Sub-assembly + label check',
        description: 'Assemble a base, fit a labelled cover, then scan the finished-unit label.',
        category: 'Assembly',
        doc: {
            name: 'Sub-assembly + label check',
            description: 'Mixed verification: an activity step, a labelled component step, and a final label/barcode scan of the finished unit.',
            cycleTimeTargetS: 90,
            steps: [
                step(1, 'Place base into fixture', { expectedAction: 'place', standardTimeS: 10, acceptableTimeS: 20 }),
                pick(2, 'Fit cover (read cover label)', comp('COVER-A1', 'Housing cover A1', 1), { std: 12, acc: 25 }),
                step(3, 'Scan finished-unit label', {
                    expectedAction: 'scan',
                    verifyBy: 'label',
                    critical: true,
                    standardTimeS: 6,
                    acceptableTimeS: 12,
                    components: [comp('UNIT-A1', 'Finished unit A1', 1)],
                }),
            ],
        },
    },
    {
        key: 'setup-precheck',
        name: 'Setup / PPE pre-check',
        description: 'Start-of-shift checks: PPE on, bench clear, tools present.',
        category: 'Setup',
        doc: {
            name: 'Setup / PPE pre-check',
            description: 'A short start-of-shift routine verified by activity in each ROI. No parts to read — a good first template to see a cycle run.',
            cycleTimeTargetS: 30,
            steps: [
                step(1, 'Put on gloves', { expectedAction: 'other', standardTimeS: 5, acceptableTimeS: 10 }),
                step(2, 'Clear the bench', { expectedAction: 'other', standardTimeS: 8, acceptableTimeS: 15 }),
                step(3, 'Confirm tools present', { expectedAction: 'inspect', critical: true, standardTimeS: 6, acceptableTimeS: 12 }),
            ],
        },
    },
];
export function findTemplate(key) {
    return PROCGUARD_TEMPLATES.find((t) => t.key === key);
}
//# sourceMappingURL=procguard-templates.js.map